import { Button, Icon } from '@/atoms';
import { LinkButton, LinkButtonProps } from '@/bloks/Button';
import { GlobalMenuPanelStoryblok } from '@/components';
import { useSearchV2 } from '@/contexts/search/AddSearchProviderV2';
import { ThemeProvider } from '@/contexts/theme';
import { getThemeInfo } from '@/themes';
import { cls } from '@/utils';
import React, { useState } from 'react';
import { t } from 'ttag';
import { LinkGroup } from '../LinkGroup/LinkGroup';
import { Panel } from '../Panel/Panel';
import styles from './MenuContentMobile.module.scss';

interface Props {
	panelTitles?: string[];
	panels: GlobalMenuPanelStoryblok[];
	activePanel: GlobalMenuPanelStoryblok | null;
	setActivePanel: (panel: GlobalMenuPanelStoryblok | null) => void;
	footerLinks?: LinkButtonProps[];
	className?: string;
}

export const MenuContentMobile: React.FC<Props> = ({
	panelTitles,
	panels,
	activePanel,
	setActivePanel,
	footerLinks,
	className,
}) => {
	const { isSearchOpen } = useSearchV2();
	const theme = 'menuContent';
	const themeInfo = getThemeInfo(theme);
	const [activeLevel, setActiveLevel] = useState<1 | 2>(1);
	const firstLevel = { panelTitles, footerLinks };

	const handleLevelChange = (level: 1 | 2, title?: string) => {
		setActiveLevel(level);

		if (level === 1) {
			setActivePanel(null);
		}

		if (level === 2 && title) {
			const panel = panels.find((p) => p.title === title);
			if (panel) {
				setActivePanel(panel);
			}
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<div className={cls(styles.menuContent, !isSearchOpen && themeInfo.styles.bgColor, className)}>
				<Panel
					level={activeLevel}
					setActiveLevel={setActiveLevel}
					active={activeLevel === 1}
					className={styles.levelOne}
				>
					<div className={styles.panelTitles}>
						{firstLevel.panelTitles?.map((title) => (
							<Button
								variant="link"
								size="large"
								className={styles.levelButton}
								classNameInner={styles.levelButtonInner}
								key={title}
								onClick={() => handleLevelChange(2, title)}
							>
								<span>{title}</span>
								<Icon name="arrow-right" />
							</Button>
						))}
					</div>
					<div className={styles.footerLinks}>
						<LinkButton href="https://apps.fortnox.se/fs/fs/login.php" variant="link" className={styles.loginButton}>
							{t`Logga in`}
						</LinkButton>
						{firstLevel.footerLinks?.map((link) => <LinkButton key={link._uid} {...link} variant="link" />)}
					</div>
				</Panel>

				<Panel
					level={activeLevel}
					setActiveLevel={setActiveLevel}
					currentTitle={activePanel?.title}
					active={activeLevel === 2}
					className={cls(
						styles.levelTwo,
						activeLevel === 2 && styles.slideIn,
						activePanel && activeLevel === 1 && styles.slideOut,
					)}
				>
					<div className={styles.linkGroupWrapper}>
						{activePanel?.mainLinkGroup?.map((group) => (
							<LinkGroup key={group._uid} {...LinkGroup.blokProps({ blok: group })} main />
						))}
						{activePanel?.linkGroup?.map((group) => (
							<LinkGroup key={group._uid} {...LinkGroup.blokProps({ blok: group })} className={styles.linkGroup} />
						))}
					</div>
				</Panel>
			</div>
		</ThemeProvider>
	);
};
